import { useCallback, useState } from "react";
import { OrganizationType } from "../../../../../../enums/organizationTypes";
import { evaluateStpReviewRules } from "./evaluateStpReviewRules";
import { fetchCmmInputData } from "./fetchCmmInputData";
import { EvaluationApiResponse } from "./playGroundTypes";

const usePlayGround = () => {
  const [selectedOrgId, setSelectedOrgId] = useState<string>(
    OrganizationType.NYCBS_PHARMA,
  );
  const [identifierId, setIdentifierId] = useState("");
  const [jsonInput, setJsonInput] = useState("");
  const [jsonParseError, setJsonParseError] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [evaluationResult, setEvaluationResult] =
    useState<EvaluationApiResponse | null>(null);
  const [runError, setRunError] = useState<string>("");
  const [expandedStageIds, setExpandedStageIds] = useState<Set<string>>(
    new Set(),
  );

  const validateJson = useCallback((value: string): boolean => {
    if (!value.trim()) {
      setJsonParseError("");
      return false;
    }
    try {
      JSON.parse(value);
      setJsonParseError("");
      return true;
    } catch (e) {
      setJsonParseError((e as Error).message);
      return false;
    }
  }, []);

  const handleJsonInputChange = useCallback(
    (value: string) => {
      setJsonInput(value);
      validateJson(value);
    },
    [validateJson],
  );

  const isJsonValid = jsonInput.trim() !== "" && jsonParseError === "";

  const handleFetch = useCallback(async () => {
    if (!identifierId.trim() || !selectedOrgId) return;
    setIsFetching(true);
    try {
      const cmmInputData = await fetchCmmInputData(
        identifierId.trim(),
        selectedOrgId,
      );
      setJsonInput(JSON.stringify(cmmInputData, null, 2));
      setJsonParseError("");
    } catch (e) {
      setJsonParseError((e as Error).message);
    } finally {
      setIsFetching(false);
    }
  }, [identifierId, selectedOrgId]);

  const handleRun = useCallback(async () => {
    if (!isJsonValid) return;
    setIsRunning(true);
    setEvaluationResult(null);
    setRunError("");
    setExpandedStageIds(new Set());

    try {
      const payload = JSON.parse(jsonInput);
      const result: EvaluationApiResponse =
        await evaluateStpReviewRules(payload);
      setEvaluationResult(result);

      // Auto-expand steps that have an error/failure reason
      const autoExpand = new Set<string>();
      result.details?.blocks?.forEach((block) => {
        block.steps.forEach((step) => {
          if (
            step.reason &&
            (step.status === "error" || step.status === "blocked")
          ) {
            autoExpand.add(step.step_id);
          }
        });
      });
      setExpandedStageIds(autoExpand);
    } catch (e) {
      setRunError((e as Error).message);
    } finally {
      setIsRunning(false);
    }
  }, [isJsonValid, jsonInput]);

  const handleReset = useCallback(() => {
    setIdentifierId("");
    setJsonInput("");
    setJsonParseError("");
    setEvaluationResult(null);
    setRunError("");
    setExpandedStageIds(new Set());
  }, []);

  const handleRefresh = useCallback(() => {
    if (isJsonValid) {
      handleRun();
    }
  }, [isJsonValid, handleRun]);

  const toggleStageExpanded = useCallback((stageId: string) => {
    setExpandedStageIds((prev) => {
      const next = new Set(prev);
      if (next.has(stageId)) {
        next.delete(stageId);
      } else {
        next.add(stageId);
      }
      return next;
    });
  }, []);

  return {
    selectedOrgId,
    setSelectedOrgId,
    identifierId,
    setIdentifierId,
    jsonInput,
    jsonParseError,
    isFetching,
    isRunning,
    isJsonValid,
    evaluationResult,
    runError,
    expandedStageIds,
    handleJsonInputChange,
    handleFetch,
    handleRun,
    handleReset,
    handleRefresh,
    toggleStageExpanded,
  };
};

export default usePlayGround;
